import type { DocPage } from '../types'
import { media } from '../media'


export const transfer: DocPage = {
  id: 'transfer',
  title: 'Running transfers',
  description:
    'Run mapped or scratch transfers: Transfer Review fields, progress overlay, MAPPED vs SCRATCH rules, post-transfer style export, and the logger.',
  category: 'Transfer',
  sections: [
    { id: 'mapped-flow', title: 'Mapped transfer' },
    { id: 'scratch-flow', title: 'Scratch transfer' },
    { id: 'review', title: 'Transfer Review fields' },
    { id: 'rules', title: 'MAPPED vs SCRATCH rules' },
    { id: 'progress', title: 'Progress overlay' },
    { id: 'logger', title: 'Logger during transfer' },
    { id: 'styles-after', title: 'Style export after transfer' },
    { id: 'after', title: 'After transfer' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'mapped-flow',
      text: 'Mapped transfer',
    },
    {
      type: 'steps',
      items: [
        'Configure Source (format → New → connection → optional filters → model tree).',
        'Configure Target the same way so its model tree is available.',
        'Connect collections and attributes (manually or with Auto Map).',
        'Add fixed mappings, value mappings, and Connect-ID/Delete as needed.',
        'Optionally set a spatial filter on the Source map; confirm layer visibility.',
        'Click Transfer, confirm Transfer Review, and monitor the progress overlay and logger.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'scratch-flow',
      text: 'Scratch transfer',
    },
    {
      type: 'steps',
      items: [
        'Configure Source as usual.',
        'Choose a Target format → New and set the output path or connection (Scratch Transfer Details banner).',
        'Validate scratch-specific fields (for example PostGIS Database / Schema / Table (base)).',
        'Optionally add fixed mappings on source/logical attributes (value mapping unavailable).',
        'Run Transfer — X-Translator creates target schema/layers from the source.',
        'Note: X-Format as target always uses the scratch write path.',
      ],
    },
    {
      type: 'figure',
      src: media.scratchTransferDetailsPostgis,
      alt: 'Scratch Transfer Details for PostGIS target',
      caption:
        'Figure: Scratch Transfer Details on a PostGIS Target after Connection Successful — catalog summary and Change Connection.',
      size: 'compact',
    },
    {
      type: 'heading',
      level: 2,
      id: 'review',
      text: 'Transfer Review fields',
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Screenshot tip',
      text: 'Capture the Transfer Review modal as Transfer-Review.png (formats, models, MAPPED/SCRATCH, coordinates, spatial filter) to illustrate this checklist.',
    },
    {
      type: 'table',
      headers: ['Field', 'What to verify'],
      rows: [
        ['Source Format / Source Model', 'Correct format and configuration (watch for UNSAVED).'],
        ['Target Format / Target Model', 'Correct destination; scratch path/catalog if SCRATCH.'],
        ['Mapping Type', 'MAPPED or SCRATCH.'],
        ['Coordinate conversions', 'X/Y offsets; Change when entitled.'],
        ['Spatial area filter', 'Whether a drawn filter is active and which inclusion mode you expect.'],
        [
          'Map layer visibility',
          'N collection(s) hidden (excluded from transfer) — unhide if unintentional.',
        ],
      ],
    },
    {
      type: 'callout',
      variant: 'warning',
      title: 'UNSAVED models',
      text: 'If Transfer Review shows UNSAVED, the tip Model needs to save as configuration means you should Save / Create Custom Config before relying on a named config reference.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'rules',
      text: 'MAPPED vs SCRATCH rules',
    },
    {
      type: 'table',
      headers: ['Topic', 'MAPPED', 'SCRATCH'],
      rows: [
        ['Target model', 'Existing target tree required', 'Created from source'],
        ['Connect / Auto Map', 'Yes', 'Not used for target tree mapping'],
        ['Value mapping', 'Yes', 'No — Value mapping is not available for scratch transfers'],
        ['Fixed mapping', 'Add fixed mapping', 'Set fixed value'],
        ['Connect-ID / Delete', 'When format supports', 'Generally not applicable without target keys'],
        ['Hidden map layers', 'Excluded', 'Excluded'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'progress',
      text: 'Progress overlay',
    },
    {
      type: 'paragraph',
      text: 'After you confirm Transfer Review, a transfer overlay stays visible while the job runs. Do not close the application mid-run. Watch the bottom logger for live LOGS / EVENTS / ERRORS. When the overlay completes, review counts and any error messages before opening outputs.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'logger',
      text: 'Logger during transfer',
    },
    {
      type: 'figure',
      src: media.currentTransferLogger,
      alt: 'Current transfer logger panel',
      caption:
        'Figure: Bottom logger while a transfer is active or just finished — use ERRORS first when diagnosing failures.',
    },
    {
      type: 'list',
      items: [
        'LOGS — general progress and informational lines.',
        'EVENTS — structured transfer events.',
        'ERRORS — failures and exceptions; start here when a run fails.',
        'Raise Global Log Settings to DETAILED or DEBUG temporarily if ERRORS lack detail (when entitled).',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'styles-after',
      text: 'Style export after transfer',
    },
    {
      type: 'paragraph',
      text: 'If the Target form enabled Layer style files (after transfer), sidecars and/or layer_styles writes occur after the data transfer succeeds. Confirm Output folder, QML/SLD/XStyles toggles, and Write QGIS layer_styles in DB as configured. See Styles import & export.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'after',
      text: 'After transfer',
    },
    {
      type: 'list',
      items: [
        'Check LOGS / EVENTS / ERRORS in the bottom logger.',
        'Open Transfer History for status, record counts, duration, and downloadable logs (if entitled).',
        'Verify geometry and attributes in the target store or files.',
        'Confirm style sidecars / layer_styles when export was enabled.',
        'Save a Transfer Profile if you want to reuse the setup.',
      ],
    },
  ],
}
